import React, { useState, useEffect, useReducer } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import CampaignConfigModal from "../../components/CampaignConfigModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { format, parseISO } from "date-fns";
import SettingsInputComponentIcon from "@material-ui/icons/SettingsInputComponent";

const reducer = (state, action) => {
    if (action.type === "LOAD_CAMPAIGNS") {
        const campaigns = action.payload;
        return [...campaigns];
    }

    if (action.type === "UPDATE_CAMPAIGN") {
        const campaign = action.payload;
        const campaignIndex = state.findIndex(c => c.id === campaign.id);

        if (campaignIndex !== -1) {
            state[campaignIndex] = campaign;
            return [...state];
        } else {
            return [campaign, ...state];
        }
    }

    if (action.type === "DELETE_CAMPAIGN") {
        const campaignId = parseInt(action.payload);
        return state.filter(c => c.id !== campaignId);
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    btnDownload: {
        marginRight: 10,
        color: "#FFF",
        "&:hover": {
            color: "#FFF",
        }
    }
}));

const getStatusColor = (status) => {
    const colors = {
        draft: "default",
        scheduled: "primary",
        sending: "secondary",
        paused: "default",
        done: "primary",
        error: "secondary"
    };
    return colors[status] || "default";
};

const Campaigns = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [campaigns, dispatch] = useReducer(reducer, []);
    const [campaignModalOpen, setCampaignModalOpen] = useState(false);
    const [campaignConfigModalOpen, setCampaignConfigModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        setLoading(true);
        const fetchCampaigns = async () => {
            try {
                const { data } = await api.get("/campaigns");
                dispatch({ type: "LOAD_CAMPAIGNS", payload: data });
                setLoading(false);
            } catch (err) {
                toastError(err);
                setLoading(false);
            }
        };
        fetchCampaigns();
    }, []);

    useEffect(() => {
        const socket = openSocket();

        console.log("[Campaigns] WebSocket conectado");

        socket.on("campaign", data => {
            console.log("[Campaigns] Evento recebido:", data);

            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_CAMPAIGN", payload: data.campaign });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_CAMPAIGN", payload: data.campaignId });
            }
        });

        return () => {
            console.log("[Campaigns] WebSocket desconectando");
            socket.disconnect();
        };
    }, []);

    const handleOpenCampaignModal = () => {
        setSelectedCampaign(null);
        setCampaignModalOpen(true);
    };

    const handleCloseCampaignModal = () => {
        setSelectedCampaign(null);
        setCampaignModalOpen(false);
    };

    const handleOpenCampaignConfigModal = (campaign) => {
        setSelectedCampaign(campaign);
        setCampaignConfigModalOpen(true);
    }

    const handleCloseCampaignConfigModal = () => {
        setSelectedCampaign(null);
        setCampaignConfigModalOpen(false);
    }

    const handleEditCampaign = campaign => {
        setSelectedCampaign(campaign);
        setCampaignModalOpen(true);
    };

    const handleDeleteCampaign = async campaignId => {
        try {
            await api.delete(`/campaigns/${campaignId}`);
            toast.success(i18n.t("campaigns.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    const handleStartCampaign = async campaignId => {
        try {
            await api.post(`/campaigns/${campaignId}/start`);
            toast.success(i18n.t("campaigns.toasts.started"));
        } catch (err) {
            toastError(err);
        }
    };

    const handleControlCampaign = async (campaignId, action) => {
        try {
            await api.post(`/campaigns/${campaignId}/control`, { action });
            toast.success(i18n.t("campaigns.toasts.controlled"));
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
    };

    const handleCloneCampaign = async campaignId => {
        try {
            const { data } = await api.post(`/campaigns/${campaignId}/clone`);
            toast.success(i18n.t("campaigns.toasts.cloned"));
            // Abrir modal para editar a campanha clonada
            setSelectedCampaign(data);
            setCampaignModalOpen(true);
        } catch (err) {
            toastError(err);
        }
    };

    const openConfirmModal = (action, campaign) => {
        setConfirmAction({ action, campaign });
        setConfirmModalOpen(true);
    };

    return (
        <MainContainer>
            <CampaignConfigModal
                open={campaignConfigModalOpen}
                onClose={handleCloseCampaignConfigModal}
                campaignId={selectedCampaign?.id}
            />
            <CampaignModal
                open={campaignModalOpen}
                onClose={handleCloseCampaignModal}
                campaignId={selectedCampaign?.id}
            />
            <ConfirmationModal
                title={
                    confirmAction?.action === "delete"
                        ? i18n.t("campaigns.confirmationModal.deleteTitle")
                        : confirmAction?.action === "stop"
                            ? i18n.t("campaigns.confirmationModal.pauseTitle")
                            : i18n.t("campaigns.confirmationModal.deleteTitle")
                }
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => {
                    if (confirmAction?.action === "delete") {
                        handleDeleteCampaign(confirmAction.campaign.id);
                    } else if (confirmAction?.action === "stop") {
                        handleControlCampaign(confirmAction.campaign.id, "stop");
                    } else if (confirmAction?.action === "delete_campaign") {
                        // Deleta a campanha remotamente e depois localmente, via handleDeleteCampaign que agora trata a remoção local e a requisição
                        handleControlCampaign(confirmAction.campaign.id, "delete");
                    }
                }}
            >
                {confirmAction?.action === "delete"
                    ? i18n.t("campaigns.confirmationModal.deleteMessage")
                    : i18n.t("campaigns.confirmationModal.pauseMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("campaigns.title")}</Title>
                <Button
                    variant="contained"
                    color="primary"
                    href="https://zapatendimento.com.br/excel/modelo.csv"
                    target="_blank"
                    className={classes.btnDownload}
                    startIcon={<CloudDownloadIcon />}
                >
                    {i18n.t("campaigns.buttons.downloadModel")}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenCampaignModal}
                >
                    {i18n.t("campaigns.buttons.add")}
                </Button>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">{i18n.t("campaigns.table.name")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.status")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.contacts")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.sent")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.delivered")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.read")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.failed")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.createdAt")}</TableCell>
                            <TableCell align="center">{i18n.t("campaigns.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {campaigns.map(campaign => (
                                <TableRow key={campaign.id}>
                                    <TableCell align="center">{campaign.name}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={i18n.t(`campaigns.status.${campaign.status}`)}
                                            color={getStatusColor(campaign.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">{campaign.totalContacts}</TableCell>
                                    <TableCell align="center">{campaign.sentCount || 0}</TableCell>
                                    <TableCell align="center">{campaign.deliveredCount || 0}</TableCell>
                                    <TableCell align="center">{campaign.readCount || 0}</TableCell>
                                    <TableCell align="center">{campaign.failedCount || 0}</TableCell>
                                    <TableCell align="center">
                                        {format(parseISO(campaign.createdAt), "dd/MM/yyyy HH:mm")}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={i18n.t("campaigns.buttons.config")}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenCampaignConfigModal(campaign)}
                                            >
                                                <SettingsInputComponentIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={i18n.t("campaigns.buttons.view")}>
                                            <IconButton size="small" onClick={() => handleEditCampaign(campaign)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {campaign.status === "draft" && (
                                            <>
                                                <Tooltip title={i18n.t("campaigns.buttons.edit")}>
                                                    <IconButton size="small" onClick={() => handleEditCampaign(campaign)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={i18n.t("campaigns.buttons.start")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleStartCampaign(campaign.id)}
                                                    >
                                                        <PlayArrowIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                        {["scheduled", "sending"].includes(campaign.status) && (
                                            <Tooltip title={i18n.t("campaigns.buttons.pause")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openConfirmModal("stop", campaign)}
                                                >
                                                    <PauseIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {campaign.status === "paused" && (
                                            <Tooltip title={i18n.t("campaigns.buttons.continue")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleControlCampaign(campaign.id, "continue")}
                                                >
                                                    <PlayArrowIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {(campaign.status === "scheduled" || campaign.status === "sending" || campaign.status === "paused") && (
                                            <Tooltip title="Excluir da Plataforma">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleControlCampaign(campaign.id, "delete")}
                                                    style={{ color: "var(--danger)" }}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={i18n.t("campaigns.buttons.clone")}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCloneCampaign(campaign.id)}
                                            >
                                                <FileCopyIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={i18n.t("campaigns.buttons.delete")}>
                                            <IconButton
                                                size="small"
                                                onClick={() => openConfirmModal("delete", campaign)}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={9} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default Campaigns;
